import {
  endOfMonth,
  endOfWeek,
  isThisMonth,
  isThisWeek,
  isToday,
  isWithinInterval,
  isYesterday,
  startOfMonth,
  startOfWeek,
  subDays,
} from 'date-fns';
import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { t } from 'i18next';
import { v6 as uuid } from 'uuid';
import { decodeHistoryFromStorage, encodeHistoryForStorage } from '@/agentBuilder/agentBuilder.history.persistence';
import {
  AgentBuilderRunStartError,
  CONTEXT_EDGE_MARKER,
  DEFAULT_EDGE_MARKER,
  FALLBACK_TOOL_ICON,
  TOOL_PREFIX_ICONS,
  isUIOnlyNodeType,
} from '@/agentBuilder/agentBuilder.types';
import { parseSeeqContentReference } from '@/agentBuilder/agentBuilderContentSelection.utilities';
import { getWorkbookById, getWorksheets, headers } from '@/aiAssistant/aiAssistant.utilities';
import { sqLicenseManagementStore, sqWorkbenchStore } from '@/core/core.stores';
import { Feature } from '@/licenseManagement/licenseManagement.constants';
import { hasValidFeatureInLicense } from '@/licenseManagement/licenseManagement.utilities';
import { APPSERVER_API_CONTENT_TYPE } from '@/main/app.constants';
import { isTrustedUser } from '@/services/authorization.service';
import { agentBuilderEnabled } from '@/services/systemConfiguration.utilities';
import { getOriginLabel } from '@/utilities/http.utilities';
import { logError } from '@/utilities/logger';
import { getStorageSafely } from '@/utilities/storage.utilities';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import type { NavigateFunction } from 'react-router-dom';
import type {
  AgentBuilderEdge,
  AgentBuilderGraph,
  AgentBuilderGraphMeta,
  AgentBuilderGraphSnapshot,
  AgentBuilderHistoryEntry,
  AgentBuilderListItem,
  AgentBuilderMeta,
  AgentBuilderNode,
  AgentBuilderOrigin,
  AgentBuilderRunStartErrorDetail,
  AgentBuilderRunStartPayload,
  AgentBuilderRunStatus,
  AgentBuilderTemplateListItem,
  AgentBuilderVersion,
  AgentNodeData,
  BuildSeeqContextArgs,
  BuildSeeqContextResult,
  CoreCustomAgentOutput,
  CoreCustomAgentSummary,
  CoreCustomAgentVersion,
  GetAgentBuilderOptions,
  NodeCapabilities,
  NodePaletteItem,
  SeeqContextPayload,
  SeeqNodeData,
  StartNodeData,
  WorkflowSnapshot,
} from '@/agentBuilder/agentBuilder.types';

const FOLDER_SEEQ_CONTEXT_TARGET_TYPES = new Set(['search-seeq', 'create-seeq-content']);
const AGENT_BUILDER_SEEQ_CONTENT_TYPES = [
  SeeqNames.Types.Analysis,
  SeeqNames.Types.Topic,
  SeeqNames.Types.Vantage,
  SeeqNames.Types.Project,
  SeeqNames.Types.Folder,
];

// ============================================================================
// Nodes Palette Utilities
// ============================================================================

/**
 * Returns Tailwind classes for special origin icons (fc-analysis, fc-report) that need
 * colored backgrounds with white text to match Seeq branding.
 * These icons represent different Seeq origins with unique color associations.
 */
export const getIconExtraClasses = (icon: string): string => {
  if (icon === 'fc-analysis') {
    return 'tw:text-white tw:bg-sq-analysis tw:rounded tw:p-0.5';
  }
  if (icon === 'fc-report') {
    return 'tw:text-white tw:bg-sq-report tw:rounded tw:p-0.5';
  }
  return '';
};

/**
 * Resolves a Font Awesome icon for a tool name by splitting on `_` or `-`,
 * lowercasing the first token, and looking it up in `TOOL_PREFIX_ICONS`.
 * Falls back to `FALLBACK_TOOL_ICON` when no prefix matches.
 */
export const getToolIcon = (toolName: string): string => {
  const prefix = toolName.split(/[_-]/, 1)[0]?.toLowerCase() ?? '';
  return TOOL_PREFIX_ICONS[prefix] ?? FALLBACK_TOOL_ICON;
};

/**
 * Filter palette items based on a search query.
 * Matches against the item's label, description, and type (case-insensitive).
 */
export const filterPaletteItems = (items: NodePaletteItem[], searchQuery: string): NodePaletteItem[] => {
  if (!searchQuery.trim()) {
    return items;
  }

  const normalizedQuery = searchQuery.toLowerCase().trim();

  return items.filter((item) => {
    const labelMatches = item.label.toLowerCase().includes(normalizedQuery);
    const descriptionMatches = item.description.toLowerCase().includes(normalizedQuery);
    const typeMatches = item.type.toLowerCase().includes(normalizedQuery);

    return labelMatches || descriptionMatches || typeMatches;
  });
};

export type CustomerConfigCapabilities = {
  anyBlobStorageConfigured: boolean;
};

export async function getCustomerConfigCapabilities(): Promise<CustomerConfigCapabilities> {
  try {
    const response = await fetch('/genai/config/customer/capabilities', { method: 'GET', headers: headers() });
    if (!response.ok) {
      throw new Error(`Failed to fetch customer config capabilities: ${response.status}`);
    }
    const data: unknown = await response.json();
    if (
      data &&
      typeof data === 'object' &&
      'anyBlobStorageConfigured' in data &&
      typeof (data as { anyBlobStorageConfigured?: unknown }).anyBlobStorageConfigured === 'boolean'
    ) {
      return { anyBlobStorageConfigured: (data as { anyBlobStorageConfigured: boolean }).anyBlobStorageConfigured };
    }
    return { anyBlobStorageConfigured: false };
  } catch (error) {
    logError('Error fetching customer config capabilities:', String(error));
    return { anyBlobStorageConfigured: false };
  }
}

// ============================================================================
// Workflow Run API Utilities
// ============================================================================

export function agentBuilderAvailable(): boolean {
  return (
    isTrustedUser() &&
    agentBuilderEnabled() &&
    hasValidFeatureInLicense(sqLicenseManagementStore.getState().license, Feature.Agent_Builder)
  );
}

const coreJsonHeaders = (): Record<string, string> => ({
  ...coreHeaders(),
  'Content-Type': APPSERVER_API_CONTENT_TYPE,
});

const coreHeaders = (): Record<string, string> => ({
  ...headers(),
  Accept: APPSERVER_API_CONTENT_TYPE,
});

function mapCoreSummaryToListItem(item: CoreCustomAgentSummary): AgentBuilderListItem {
  return {
    id: item.id,
    name: item.name,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    hasDraft: Boolean(item.draft),
    draftBaseVersionId: item.draft?.baseVersionId ?? null,
    draftUpdatedAt: item.draft?.updatedAt,
    effectivePermissions: item.effectivePermissions,
    isOwner: item.isOwner,
    isSharedWithCurrentUser: item.isSharedWithCurrentUser,
    creatorName: item.creatorName,
  };
}

function parseCoreWorkflowPayload(workflowJson: string): {
  nodes: AgentBuilderNode[];
  edges: AgentBuilderEdge[];
  meta: unknown;
} {
  const payload = JSON.parse(workflowJson) as Record<string, unknown>;
  const nodes = Array.isArray(payload.nodes) ? (payload.nodes as AgentBuilderNode[]) : [];
  const edges = Array.isArray(payload.edges) ? (payload.edges as AgentBuilderEdge[]) : [];
  const meta = payload.meta;
  return { nodes, edges, meta };
}

function mapCoreOutputToAgentBuilder(output: CoreCustomAgentOutput): AgentBuilderGraph {
  const parsed = parseCoreWorkflowPayload(output.workflow);
  const payloadMeta = parsed.meta && typeof parsed.meta === 'object' ? (parsed.meta as AgentBuilderMeta) : {};
  return {
    id: output.id,
    name: output.name,
    nodes: parsed.nodes,
    edges: parsed.edges,
    meta: {
      ...payloadMeta,
      createdAt: output.meta.createdAt,
      updatedAt: output.meta.updatedAt,
      hasDraft: Boolean(output.meta.draft),
      draftBaseVersionId: output.meta.draft?.baseVersionId ?? null,
      draftUpdatedAt: output.meta.draft?.updatedAt,
    },
    effectivePermissions: output.effectivePermissions,
  };
}

function mapCoreVersionToAgentBuilderVersion(version: CoreCustomAgentVersion): AgentBuilderVersion {
  return {
    versionId: version.revision.id,
    createdAt: version.revision.createdAt,
    createdBy: version.revision.createdBy,
    production: version.production,
  };
}

async function createAgentBuilderDraft(id: string, baseVersionId?: string | null) {
  const createDraftBody = baseVersionId ? { baseVersionId } : {};
  return fetch(`/api/custom-agents/${id}/draft`, {
    method: 'POST',
    headers: coreJsonHeaders(),
    body: JSON.stringify(createDraftBody),
  });
}

export async function getAgentBuilderList(): Promise<AgentBuilderListItem[]> {
  try {
    const response = await fetch('/api/custom-agents', { method: 'GET', headers: coreHeaders() });
    if (!response.ok) {
      throw new Error(`Failed to fetch agent builder list: ${response.status}`);
    }
    const data = (await response.json()) as CoreCustomAgentSummary[];
    return data.map(mapCoreSummaryToListItem);
  } catch (error) {
    logError('Error fetching agent builder list:', String(error));
    return [];
  }
}

/**
 * Returns `null` (not an empty list) when the request fails so callers can
 * distinguish "user has no drafts" from "fetch failed" — destructive
 * consumers like {@link pruneOrphanAgentBuilderEditorHistory} must not treat
 * a transient failure as an empty access list.
 */
export async function getAgentBuilderDrafts(): Promise<AgentBuilderListItem[] | null> {
  try {
    const response = await fetch('/api/custom-agents?versioned=false', { method: 'GET', headers: coreHeaders() });
    if (!response.ok) {
      throw new Error(`Failed to fetch agent builder drafts: ${response.status}`);
    }
    const data = (await response.json()) as CoreCustomAgentSummary[];
    return data.map(mapCoreSummaryToListItem);
  } catch (error) {
    logError('Error fetching agent builder drafts:', String(error));
    return null;
  }
}

/**
 * Returns `null` (not an empty list) when the request fails so callers can
 * distinguish "user has no published agents" from "fetch failed" — see
 * {@link getAgentBuilderDrafts}.
 */
export async function getAgentBuilderPublished(): Promise<AgentBuilderListItem[] | null> {
  try {
    const response = await fetch('/api/custom-agents?versioned=true', { method: 'GET', headers: coreHeaders() });
    if (!response.ok) {
      throw new Error(`Failed to fetch agent builder published: ${response.status}`);
    }
    const data = (await response.json()) as CoreCustomAgentSummary[];
    return data.map(mapCoreSummaryToListItem);
  } catch (error) {
    logError('Error fetching agent builder published:', String(error));
    return null;
  }
}

export async function getAgentBuilderProductionPublished(): Promise<AgentBuilderListItem[] | null> {
  try {
    const response = await fetch('/api/custom-agents?versioned=true&production=set', {
      method: 'GET',
      headers: coreHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch production agent builders: ${response.status}`);
    }
    const data = (await response.json()) as CoreCustomAgentSummary[];
    return data.map(mapCoreSummaryToListItem);
  } catch (error) {
    logError('Error fetching production agent builders:', String(error));
    return null;
  }
}

export async function getAgentBuilderTemplates(): Promise<AgentBuilderTemplateListItem[]> {
  try {
    const response = await fetch('/genai/agent-builder/templates', { method: 'GET', headers: headers() });
    if (!response.ok) {
      throw new Error(`Failed to fetch agent builder templates: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    logError('Error fetching agent builder templates:', String(error));
    return [];
  }
}

/**
 * Normalize a raw node-capabilities entry into the {@link NodeCapabilities} shape.
 *
 * Tolerates the legacy array-only payload (`["tool", ...]`) in case a stale backend is
 * hit during a rolling deploy, treating it as tools with no built-in skills.
 */
function normalizeNodeCapabilities(value: unknown): NodeCapabilities {
  if (Array.isArray(value)) {
    return { tools: value as string[], builtinSkills: [] };
  }
  const entry = (value ?? {}) as Partial<NodeCapabilities>;
  return {
    tools: Array.isArray(entry.tools) ? entry.tools : [],
    builtinSkills: Array.isArray(entry.builtinSkills) ? entry.builtinSkills : [],
  };
}

/**
 * GET /genai/agent-builder/node-capabilities
 *
 * Returns a map of canonical node type to the tools it exposes (e.g. `data_timeseries`,
 * `files_read`) and its built-in core skills (e.g. `get_time_series_data`). Drives the
 * read-only "Capabilities & Tools" section in node inspectors. Tool names are LLM-facing
 * identifiers and are intentionally surfaced to end users without translation.
 */
export async function getNodeCapabilities(): Promise<Record<string, NodeCapabilities>> {
  try {
    const response = await fetch('/genai/agent-builder/node-capabilities', { method: 'GET', headers: headers() });
    if (!response.ok) {
      throw new Error(`Failed to fetch node capabilities: ${response.status}`);
    }
    const raw = (await response.json()) as Record<string, unknown>;
    return Object.fromEntries(
      Object.entries(raw).map(([nodeType, value]) => [nodeType, normalizeNodeCapabilities(value)]),
    );
  } catch (error) {
    logError('Error fetching node capabilities:', String(error));
    return {};
  }
}

export async function getAgentBuilderTemplate(id: string): Promise<AgentBuilderGraph | null> {
  try {
    const response = await fetch(`/genai/agent-builder/templates/${id}`, { method: 'GET', headers: headers() });
    if (!response.ok) {
      throw new Error(`Failed to fetch agent builder template: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    logError(`Error fetching agent builder template ${id}:`, String(error));
    return null;
  }
}

export async function instantiateAgentBuilderTemplate(id: string): Promise<AgentBuilderGraph | null> {
  try {
    const response = await fetch(`/genai/agent-builder/templates/${id}/instantiate`, {
      method: 'POST',
      headers: {
        ...headers(),
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: '{}',
    });
    if (!response.ok) {
      throw new Error(`Failed to instantiate agent builder template: ${response.status}`);
    }
    const data = (await response.json()) as CoreCustomAgentOutput;
    return mapCoreOutputToAgentBuilder(data);
  } catch (error) {
    logError(`Error instantiating agent builder template ${id}:`, String(error));
    return null;
  }
}

export async function getAgentBuilder(id: string, options?: GetAgentBuilderOptions): Promise<AgentBuilderGraph | null> {
  try {
    if (options?.versionId) {
      const response = await fetch(`/api/custom-agents/${id}/versions/${options.versionId}`, {
        method: 'GET',
        headers: coreHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch version: ${response.status}`);
      }
      const data = (await response.json()) as CoreCustomAgentOutput;
      return mapCoreOutputToAgentBuilder(data);
    }

    if (options?.production) {
      // Fetch versions to find the production version
      const versions = await getAgentBuilderVersions(id);
      const productionVersion = versions.find((v) => v.production === true);
      if (!productionVersion) {
        return null;
      }
      // Fetch the production version's graph
      const response = await fetch(`/api/custom-agents/${id}/versions/${productionVersion.versionId}`, {
        method: 'GET',
        headers: coreHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch production version: ${response.status}`);
      }
      const data = (await response.json()) as CoreCustomAgentOutput;
      return mapCoreOutputToAgentBuilder(data);
    }

    // Default: fetch current state
    const response = await fetch(`/api/custom-agents/${id}`, { method: 'GET', headers: coreHeaders() });
    if (!response.ok) {
      throw new Error(`Failed to fetch agent builder: ${response.status}`);
    }
    const data = (await response.json()) as CoreCustomAgentOutput;
    return mapCoreOutputToAgentBuilder(data);
  } catch (error) {
    logError(`Error fetching agent builder ${id}:`, String(error));
    return null;
  }
}

export async function createAgentBuilder(
  name: string,
  graph: Omit<AgentBuilderGraph, 'id'>,
): Promise<AgentBuilderGraph | null> {
  try {
    const response = await fetch('/api/custom-agents', {
      method: 'POST',
      headers: coreJsonHeaders(),
      body: JSON.stringify({
        name,
        workflow: JSON.stringify(graph),
      }),
    });
    if (!response.ok) {
      throw new Error(`Failed to create agent builder: ${response.status}`);
    }
    const data = (await response.json()) as CoreCustomAgentOutput;
    return mapCoreOutputToAgentBuilder(data);
  } catch (error) {
    logError('Error creating agent builder:', String(error));
    return null;
  }
}

export async function updateAgentBuilder(
  id: string,
  updates: {
    name?: string;
    nodes?: AgentBuilderNode[];
    edges?: AgentBuilderEdge[];
    meta?: AgentBuilderMeta;
    isDraftSave?: boolean;
  },
): Promise<AgentBuilderGraph | null> {
  try {
    const shouldWriteWorkflow = updates.nodes !== undefined || updates.edges !== undefined;
    const isNameOnlyUpdate = updates.name !== undefined && !shouldWriteWorkflow && !updates.isDraftSave;
    const shouldEnsureDraft = shouldWriteWorkflow || updates.isDraftSave;

    const payload: Record<string, unknown> = {};
    if (updates.name !== undefined) {
      payload.name = updates.name;
    }
    if (shouldWriteWorkflow) {
      payload.workflow = JSON.stringify({
        nodes: updates.nodes ?? [],
        edges: updates.edges ?? [],
        meta: updates.meta ?? {},
      });
    }

    if (isNameOnlyUpdate) {
      const response = await fetch(`/api/custom-agents/${id}`, {
        method: 'PUT',
        headers: coreJsonHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`Failed to update agent builder: ${response.status}`);
      }
      const data = (await response.json()) as CoreCustomAgentOutput;
      return mapCoreOutputToAgentBuilder(data);
    }

    const updateDraft = () =>
      fetch(`/api/custom-agents/${id}/draft`, {
        method: 'PUT',
        headers: coreJsonHeaders(),
        body: JSON.stringify(payload),
      });

    let response = await updateDraft();

    if (!response.ok && shouldEnsureDraft && response.status === 404) {
      const createDraftResponse = await createAgentBuilderDraft(id, updates.meta?.draftBaseVersionId ?? null);

      if (!createDraftResponse.ok && createDraftResponse.status !== 409) {
        throw new Error(`Failed to create agent builder draft: ${createDraftResponse.status}`);
      }

      response = await updateDraft();
    }

    if (!response.ok) {
      throw new Error(`Failed to update agent builder: ${response.status}`);
    }
    const data = (await response.json()) as CoreCustomAgentOutput;
    return mapCoreOutputToAgentBuilder(data);
  } catch (error) {
    logError(`Error updating agent builder ${id}:`, String(error));
    return null;
  }
}

export async function duplicateAgentBuilder(id: string): Promise<AgentBuilderGraph | null> {
  try {
    const [sourceGraph, sourceVersions] = await Promise.all([getAgentBuilder(id), getAgentBuilderVersions(id)]);
    if (!sourceGraph) {
      throw new Error('Failed to fetch source agent builder');
    }

    const copiedName = `${sourceGraph.name} - Copy`;
    const created = await createAgentBuilder(copiedName, {
      name: copiedName,
      nodes: sourceGraph.nodes,
      edges: sourceGraph.edges,
      meta: { hasDraft: false },
    });
    if (!created) {
      throw new Error('Failed to create duplicated agent builder');
    }

    // Versions API returns newest-first for the selector UI, but each publish assigns the next
    // ascending version number. Replay oldest-first so copy v1 matches source v1 content.
    const versionsOldestFirst = [...sourceVersions].reverse();
    for (const version of versionsOldestFirst) {
      const versionGraph = await getAgentBuilder(id, { versionId: version.versionId });
      if (versionGraph) {
        await updateAgentBuilder(created.id, {
          nodes: versionGraph.nodes,
          edges: versionGraph.edges,
          meta: versionGraph.meta,
          isDraftSave: true,
        });

        await publishAgentBuilderVersion(created.id);
      }
    }

    return created;
  } catch (error) {
    logError(`Error duplicating agent builder ${id}:`, String(error));
    return null;
  }
}

export async function deleteAgentBuilder(id: string): Promise<boolean> {
  try {
    const response = await fetch(`/api/custom-agents/${id}`, {
      method: 'DELETE',
      headers: coreHeaders(),
    });
    if (response.ok) {
      // Drop the per-user editor history entry immediately on a successful
      // delete. Without this the entry leaks until the next dashboard load
      // sweep, which is surprising when the user expects "delete" to leave
      // nothing behind for this agent.
      const currentUserId = sqWorkbenchStore.currentUser?.id;
      if (currentUserId) {
        clearAgentBuilderEditorHistory(currentUserId, id);
      }
    }
    return response.ok;
  } catch (error) {
    logError(`Error deleting agent builder ${id}:`, String(error));
    return false;
  }
}

export async function discardAgentBuilderDraft(id: string): Promise<boolean> {
  try {
    const response = await fetch(`/api/custom-agents/${id}/draft`, {
      method: 'DELETE',
      headers: coreHeaders(),
    });
    return response.ok;
  } catch (error) {
    logError(`Error discarding draft for agent builder ${id}:`, String(error));
    return false;
  }
}

// Prefix used for every localStorage key that holds an agent builder
// editor history payload. Kept in one place so the orphan-cleanup sweep on
// the dashboard can enumerate every entry deterministically.
const EDITOR_HISTORY_KEY_PREFIX = 'sq.agentBuilder.editorHistory';

/**
 * Compose the localStorage key for a given user + agent builder. Scoping
 * by user matches the previous backend per-user constraint and is the
 * correct boundary for shared-browser scenarios where multiple Seeq users
 * sign in from the same profile.
 */
export function buildAgentBuilderEditorHistoryKey(userId: string, agentBuilderId: string): string {
  return `${EDITOR_HISTORY_KEY_PREFIX}.${userId}.${agentBuilderId}`;
}

/**
 * Returns the prefix shared by every editor history key for a given user.
 * Used by the orphan cleanup pass to enumerate this user's entries without
 * touching keys that belong to a different account on the same browser.
 */
export function getAgentBuilderEditorHistoryKeyPrefix(userId: string): string {
  return `${EDITOR_HISTORY_KEY_PREFIX}.${userId}.`;
}

/**
 * Synchronous, cheap check for whether a persisted editor history entry
 * exists for this user + agent (a single localStorage key lookup, no parse).
 *
 * The rehydrate effect uses this to gate on entry existence rather than on
 * `meta.hasDraft`: a never-published agent reports `hasDraft: false` even
 * while the user is actively editing a draft, so gating on `hasDraft` skipped
 * rehydrate for that case. Entry existence is the correct signal — the write
 * side persists during editing and the lifecycle clears the entry on
 * revert/publish/delete — and it keeps the no-entry case as cheap as the
 * `hasDraft` gate was (a synchronous bail before any async decode work).
 */
export function hasAgentBuilderEditorHistory(userId: string, agentBuilderId: string): boolean {
  return getStorageSafely().getItem(buildAgentBuilderEditorHistoryKey(userId, agentBuilderId)) !== null;
}

/**
 * Maximum age in milliseconds for a persisted editor history entry before
 * the dashboard sweep evicts it. 30 days = 30 * 24 * 60 * 60 * 1000.
 */
export const EDITOR_HISTORY_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Outcome of a successful persist write, suitable for emitting in telemetry.
 */
export type SaveAgentBuilderEditorHistoryResult = {
  payloadBytes: number;
  diffCount: number;
  largestDiffBytes: number;
};

/**
 * Load the caller's persisted agent builder editor undo/redo history from
 * localStorage, decoding the diff chain against the live canvas state.
 *
 * Returns null when:
 *   - no entry exists,
 *   - the entry can't be JSON-parsed (corrupt entry, self-healed by clearing),
 *   - the payload shape is wrong (e.g., old-shape pre-diff payloads),
 *   - the diff chain fails integrity checks (anchor mismatch, hash mismatch,
 *     unpatch throw).
 *
 * In every failure case the localStorage entry is removed so subsequent
 * mounts start clean.
 */
export async function loadAgentBuilderEditorHistory(
  userId: string,
  agentBuilderId: string,
  currentCanvasState: WorkflowSnapshot,
): Promise<{ pastEntries: AgentBuilderHistoryEntry[]; predecessorState: WorkflowSnapshot } | null> {
  const raw = getStorageSafely().getItem(buildAgentBuilderEditorHistoryKey(userId, agentBuilderId));
  if (!raw) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    logError(
      `Failed to parse persisted editor history for agent builder ${agentBuilderId}`,
      error instanceof Error ? error.message : String(error),
    );
    clearAgentBuilderEditorHistory(userId, agentBuilderId);
    return null;
  }

  const decoded = await decodeHistoryFromStorage(parsed, currentCanvasState);
  if (!decoded) {
    clearAgentBuilderEditorHistory(userId, agentBuilderId);
    return null;
  }
  return decoded;
}

/**
 * Persist the caller's agent builder editor undo/redo history to
 * localStorage. Encodes the bounded past into a diff chain anchored on
 * `firstEntryPredecessor`, runs a round-trip self-check per entry, and
 * writes if and only if every check passes.
 *
 * Returns:
 *   - the persistence telemetry summary on success,
 *   - null when encode produces null (round-trip check failed; nothing was
 *     written; the previous payload, if any, remains in place).
 *
 * Throws only if the underlying `setItem` throws (e.g., QuotaExceededError).
 * Callers should catch and log; the structural-cap design makes the throw
 * path much less likely than under the original snapshot-per-entry design.
 */
export async function saveAgentBuilderEditorHistory(
  userId: string,
  agentBuilderId: string,
  boundedPast: AgentBuilderHistoryEntry[],
  firstEntryPredecessor: WorkflowSnapshot,
  currentCanvasState: WorkflowSnapshot,
): Promise<SaveAgentBuilderEditorHistoryResult | null> {
  const payload = await encodeHistoryForStorage(boundedPast, firstEntryPredecessor, currentCanvasState);
  if (!payload) {
    return null;
  }
  const serialized = JSON.stringify(payload);
  getStorageSafely().setItem(buildAgentBuilderEditorHistoryKey(userId, agentBuilderId), serialized);

  let largestDiffBytes = 0;
  for (const diff of payload.diffs) {
    const size = JSON.stringify(diff).length;
    if (size > largestDiffBytes) {
      largestDiffBytes = size;
    }
  }
  return {
    payloadBytes: serialized.length,
    diffCount: payload.diffs.length,
    largestDiffBytes,
  };
}

/**
 * Remove the persisted editor history entry for a single agent. Called from
 * the revert, publish, and delete flows the moment the draft ends, and from
 * the dashboard sweep when an entry references an agent the user can no
 * longer access or has been dormant for too long.
 */
export function clearAgentBuilderEditorHistory(userId: string, agentBuilderId: string): void {
  getStorageSafely().removeItem(buildAgentBuilderEditorHistoryKey(userId, agentBuilderId));
}

/**
 * Walk every editor history key for the supplied user and drop any whose
 * agent id is not in `accessibleAgentBuilderIds`, **or** whose payload's
 * `lastWrittenAt` is older than `EDITOR_HISTORY_MAX_AGE_MS`. Run on
 * dashboard load so deleted/inaccessible/dormant agents don't leak storage
 * forever.
 *
 * Entries whose payload is unparseable or missing `lastWrittenAt` are
 * treated as "indefinitely old" and dropped — this doubles as a cleanup
 * path for malformed entries.
 */
export function pruneOrphanAgentBuilderEditorHistory(
  userId: string,
  accessibleAgentBuilderIds: Iterable<string>,
  now: number = Date.now(),
): void {
  const storage = getStorageSafely();
  const prefix = getAgentBuilderEditorHistoryKeyPrefix(userId);
  const accessible = new Set(accessibleAgentBuilderIds);
  const ageCutoff = now - EDITOR_HISTORY_MAX_AGE_MS;

  // Decide whether a single editor-history key should be reaped. Drops keys
  // for agents the user can no longer access, keys with no/empty value, keys
  // whose payload is unparseable or missing `lastWrittenAt`, and keys older
  // than the age cutoff.
  const shouldRemove = (key: string): boolean => {
    const agentBuilderId = key.slice(prefix.length);
    if (!accessible.has(agentBuilderId)) {
      return true;
    }
    const raw = storage.getItem(key);
    if (!raw) {
      return true;
    }
    try {
      const parsed = JSON.parse(raw) as { lastWrittenAt?: unknown };
      if (typeof parsed.lastWrittenAt === 'number') {
        return parsed.lastWrittenAt < ageCutoff;
      }
    } catch {
      // Malformed payload — fall through to drop it.
    }
    return true;
  };

  // Collect first then delete so we don't mutate the storage while
  // iterating its index (localStorage indexes shift on every removeItem).
  const keysToRemove: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key && key.startsWith(prefix) && shouldRemove(key)) {
      keysToRemove.push(key);
    }
  }

  for (const key of keysToRemove) {
    storage.removeItem(key);
  }
}

export async function getAgentBuilderVersions(id: string): Promise<AgentBuilderVersion[]> {
  try {
    const response = await fetch(`/api/custom-agents/${id}/versions`, { method: 'GET', headers: coreHeaders() });
    if (!response.ok) {
      throw new Error(`Failed to fetch versions: ${response.status}`);
    }
    const data = (await response.json()) as CoreCustomAgentVersion[];
    return data.map(mapCoreVersionToAgentBuilderVersion);
  } catch (error) {
    logError(`Error fetching versions for agent builder ${id}:`, String(error));
    return [];
  }
}

/**
 * Picks the version the selector should default to on load, matching what the backend's default
 * graph endpoint (GET /api/custom-agents/{id}) actually serves: the production version when one is
 * set, otherwise the latest. The versions list is newest-first, so versions[0] is the latest.
 * Returns undefined when there are no versions. See CRAB-61562 — without this the selector showed
 * the latest version while the canvas rendered production.
 */
export function pickDefaultSelectedVersion(versions: AgentBuilderVersion[]): AgentBuilderVersion | undefined {
  return versions.find((v) => v.production) ?? versions?.[0];
}

export async function revertAgentBuilderToVersion(id: string, versionId: string): Promise<AgentBuilderGraph | null> {
  try {
    const response = await fetch(`/api/custom-agents/${id}/revert/${versionId}`, {
      method: 'POST',
      headers: coreHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to revert agent builder: ${response.status}`);
    }
    const data = (await response.json()) as CoreCustomAgentOutput;
    return mapCoreOutputToAgentBuilder(data);
  } catch (error) {
    logError(`Error reverting agent builder ${id} to ${versionId}:`, String(error));
    return null;
  }
}

export async function publishAgentBuilderVersion(id: string): Promise<AgentBuilderVersion[]> {
  try {
    const response = await fetch(`/api/custom-agents/${id}/versions`, {
      method: 'POST',
      headers: coreHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to publish version for agent builder ${id}: ${response.status}`);
    }
    const data = (await response.json()) as CoreCustomAgentVersion[];
    return data.map(mapCoreVersionToAgentBuilderVersion);
  } catch (error) {
    logError(`Error publishing version for agent builder ${id}:`, String(error));
    return [];
  }
}

export async function setAgentBuilderVersionProduction(
  id: string,
  versionId: string,
): Promise<AgentBuilderVersion[] | []> {
  try {
    const response = await fetch(`/api/custom-agents/${id}/production`, {
      method: 'POST',
      headers: coreJsonHeaders(),
      body: JSON.stringify({ versionId }),
    });
    if (!response.ok) {
      throw new Error(`Failed to set production version for agent builder ${id} to ${versionId}: ${response.status}`);
    }
    const data = (await response.json()) as CoreCustomAgentVersion[];
    return data.map(mapCoreVersionToAgentBuilderVersion);
  } catch (error) {
    logError(`Error setting production for agent builder ${id} to ${versionId}:`, String(error));
    return [];
  }
}

export type GroupedAgentBuilderListType = {
  today: AgentBuilderListItem[];
  yesterday: AgentBuilderListItem[];
  this_week: AgentBuilderListItem[];
  last_week: AgentBuilderListItem[];
  this_month: AgentBuilderListItem[];
  last_month: AgentBuilderListItem[];
  older: AgentBuilderListItem[];
};

export const groupAgentBuilder = (items: AgentBuilderListItem[]): GroupedAgentBuilderListType => {
  const today = new Date();
  const grouped: GroupedAgentBuilderListType = {
    today: [],
    yesterday: [],
    this_week: [],
    last_week: [],
    this_month: [],
    last_month: [],
    older: [],
  };

  const determineCategory = (updatedAt: string | undefined): keyof GroupedAgentBuilderListType => {
    if (!updatedAt) return 'older';

    const updatedAtDate = new Date(updatedAt);
    if (isNaN(updatedAtDate.getTime())) return 'older';

    if (isToday(updatedAtDate)) return 'today';
    if (isYesterday(updatedAtDate)) return 'yesterday';
    if (isThisWeek(updatedAtDate, { weekStartsOn: 1 })) return 'this_week';

    const lastWeekStart = startOfWeek(subDays(today, 7), { weekStartsOn: 1 });
    const lastWeekEnd = endOfWeek(subDays(today, 7), { weekStartsOn: 1 });
    if (isWithinInterval(updatedAtDate, { start: lastWeekStart, end: lastWeekEnd })) return 'last_week';

    if (isThisMonth(updatedAtDate)) return 'this_month';

    const lastMonthStart = startOfMonth(subDays(today, 30));
    const lastMonthEnd = endOfMonth(subDays(today, 30));
    if (isWithinInterval(updatedAtDate, { start: lastMonthStart, end: lastMonthEnd })) return 'last_month';

    return 'older';
  };

  items.forEach((item) => {
    const category = determineCategory(item.updatedAt);
    grouped[category].push(item);
  });

  return grouped;
};

// Runner utility functions
export async function getOriginDetailsFromSeeqUrl(
  seeqUrl: string,
): Promise<{ url: string; label: string; type: string } | null> {
  if (!seeqUrl) return null;

  const reference = parseSeeqContentReference(seeqUrl, AGENT_BUILDER_SEEQ_CONTENT_TYPES);
  if (!reference?.workbookId) return null;

  try {
    // Get workbook details to determine type
    const workbook = await getWorkbookById(reference.workbookId);
    if (!workbook) return null;

    // Determine document type for origin
    const documentType = workbook.type || 'Analysis';

    // If we have a worksheet ID, use it directly
    if (reference.worksheetId) {
      return {
        url: `/view/workbook/${reference.workbookId}/worksheet/${reference.worksheetId}`,
        label: getOriginLabel(),
        type: documentType,
      };
    }

    // No worksheet ID - try to get the first worksheet
    try {
      const worksheetsResponse = await getWorksheets(reference.workbookId, 1);
      if (worksheetsResponse.worksheets && worksheetsResponse.worksheets.length > 0) {
        const firstWorksheet = worksheetsResponse.worksheets[0];
        return {
          url: `/view/workbook/${reference.workbookId}/worksheet/${firstWorksheet.id}`,
          label: getOriginLabel(),
          type: documentType,
        };
      }
    } catch (error) {
      console.error('Error fetching worksheets:', error);
    }

    // Fallback to basic workbook URL
    return {
      url: `/view/workbook/${reference.workbookId}`,
      label: getOriginLabel(),
      type: documentType,
    };
  } catch (error) {
    console.error('Error getting origin details from Seeq URL:', error);
    return null;
  }
}

export function getOriginFromDocumentType(documentType: string): string {
  switch (documentType) {
    case 'Vantage':
      return 'vantage';
    case 'Topic':
    case 'Report':
      return 'organizer';
    default:
      return 'workbench';
  }
}

export function toAgentType(data: AgentNodeData): string {
  if (data.preconfiguredAgentKey) return data.preconfiguredAgentKey;
  switch (data.agentType) {
    case 'modified':
      return 'formula';
    case 'custom':
      return data.preconfiguredAgentKey || 'general';
    default:
      return 'general';
  }
}

export function buildAgentPrompt(data: AgentNodeData, upstreamInputs: unknown | unknown[]): string {
  const parts: string[] = [];
  if (data.description) parts.push(String(data.description));
  if (upstreamInputs !== undefined && upstreamInputs !== null) {
    try {
      let serialized: string;
      if (Array.isArray(upstreamInputs)) {
        serialized = upstreamInputs.map((v) => (typeof v === 'string' ? v : JSON.stringify(v))).join('\n');
      } else if (typeof upstreamInputs === 'string') {
        serialized = upstreamInputs;
      } else {
        serialized = JSON.stringify(upstreamInputs);
      }
      parts.push(`Inputs:\n${serialized}`);
    } catch {
      // ignore stringify errors
    }
  }
  return parts.join('\n\n').trim();
}

/**
 * Builds a Seeq context node and connecting context edge for a target node.
 *
 * Use cases:
 * - Inspector button (ContextSection): no position provided; we auto-offset/stack near the target.
 * - Drag-to-canvas from context handle: a position is provided; we place the Seeq node exactly where the user dropped.
 *
 * Params:
 * - targetNodeId: node that should receive context.
 * - nodes/edges: current graph state (used to avoid overlap in inspector flow).
 * - newPosition: optional absolute position to honor verbatim (skip overlap logic).
 */
export function buildSeeqContextForNode({
  targetNodeId,
  nodes,
  edges,
  newPosition, // This logic is used for dragging out the context handle to the canvas (useNewNodePlaceholder.hook.ts)
}: BuildSeeqContextArgs): BuildSeeqContextResult | null {
  const currentNode = nodes.find((node) => node.id === targetNodeId);
  if (!currentNode) return null;

  // Calculate position for new node
  // If caller provided a position (drag-to-canvas), honor it verbatim and skip overlap logic.
  // Otherwise fall back to offset/stacking logic used by the inspector button flow.
  let position: { x: number; y: number };

  if (newPosition) {
    position = newPosition;
  } else {
    let xOffset = -250;
    const yOffset = 100;
    position = {
      x: currentNode.position.x + xOffset,
      y: currentNode.position.y + yOffset,
    };

    // Check if position is occupied by another node connected to the same target
    // We only care about nodes that are 'seeq' context nodes connected to our target
    const existingContextNodes = edges
      .filter(
        (e) => e.target === targetNodeId && e.data?.edgeType === 'context' && e.sourceHandle === 'seeq-context-output',
      )
      .map((e) => nodes.find((n) => n.id === e.source))
      .filter((n): n is AgentBuilderNode => !!n);

    // If there are existing context nodes, try to find a free spot
    // We'll stack them vertically or offset them slightly to avoid direct overlap
    if (existingContextNodes.length > 0) {
      // Sort by Y position to find the lowest one
      const sortedByY = existingContextNodes.sort((a, b) => a.position.y - b.position.y);
      const lowestNode = sortedByY[sortedByY.length - 1];

      // Place new node below the lowest existing context node
      if (lowestNode) {
        position = {
          x: lowestNode.position.x,
          y: lowestNode.position.y + 50, // Add vertical spacing
        };
      }
    }
  }

  const newNodeId = `seeq-${uuid()}`;
  const newNode: AgentBuilderNode = {
    id: newNodeId,
    type: 'seeq',
    position,
    data: {
      title: '',
      description: t('AGENT_BUILDER.SEEQ_URL_DESCRIPTION'),
      seeqUrl: '',
      workbookId: '',
      allowFolderContent: canTargetUseFolderSeeqContext(currentNode.type),
    } as SeeqNodeData,
  };

  const newEdge: AgentBuilderEdge = {
    id: `e-${newNodeId}-${targetNodeId}-${uuid()}`,
    source: newNodeId,
    target: targetNodeId,
    sourceHandle: 'seeq-context-output',
    targetHandle: 'agent-context',
    type: 'context',
    markerEnd: CONTEXT_EDGE_MARKER,
    data: { edgeType: 'context' },
  };

  return {
    nodes: [...nodes, newNode],
    edges: [...edges, newEdge],
    newNodeId,
  };
}

export function sortOutgoingByTargetX(
  currentId: string,
  nodes: AgentBuilderNode[],
  edges: AgentBuilderEdge[],
): AgentBuilderEdge[] {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const outgoing = edges.filter((e) => e.source === currentId);
  return outgoing.sort((a, b) => {
    const ax = nodeMap.get(a.target)?.position?.x || 0;
    const bx = nodeMap.get(b.target)?.position?.x || 0;
    return ax - bx;
  });
}

export { decodeStreamToText } from '@/aiAssistant/streaming.utilities';

// Request building utilities
export function buildAgentChatRequestHeaders(
  originDetails: { url: string; label: string; type: string } | null,
): Record<string, string> {
  const requestHeaders: Record<string, string> = { ...headers(), 'content-type': APPSERVER_API_CONTENT_TYPE };
  if (originDetails) {
    requestHeaders[SeeqNames.API.Headers.RequestOrigin] = getOriginFromDocumentType(originDetails.type);
    requestHeaders[SeeqNames.API.Headers.RequestOriginURL] = originDetails.url;
    requestHeaders[SeeqNames.API.Headers.RequestOriginLabel] = originDetails.label;
  }
  return requestHeaders;
}

export function buildAgentChatRequestBody(
  prompt: string,
  chatId: string,
  agentType: string,
): {
  prompt: string;
  chat_id: string;
  agentType: string;
  stream: boolean;
  streamThinking: boolean;
  asyncStream: boolean;
} {
  return {
    prompt,
    chat_id: chatId,
    agentType,
    stream: true,
    streamThinking: true,
    asyncStream: true,
  };
}

// Agent chat API utilities
export async function startAgentChat(
  requestHeaders: Record<string, string>,
  requestBody: {
    prompt: string;
    chat_id: string;
    agentType: string;
    stream: boolean;
    streamThinking: boolean;
    asyncStream: boolean;
  },
  signal?: AbortSignal,
): Promise<{ bot_message_id: string }> {
  const response = await fetch('/genai/llm/chat', {
    method: 'POST',
    headers: requestHeaders,
    body: JSON.stringify(requestBody),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Chat start failed: ${response.status} - ${errorText}`);
  }

  const body = await response.json();
  const botMessageId = body?.bot_message_id;

  if (!botMessageId) {
    throw new Error('No bot_message_id returned for streaming');
  }

  return { bot_message_id: botMessageId };
}

export async function getAgentChatStream(
  chatId: string,
  botMessageId: string,
  requestHeaders: Record<string, string>,
  signal?: AbortSignal,
): Promise<ReadableStream<Uint8Array> | null> {
  const streamUrl = `/genai/llm/chat/stream/${chatId}?bot_message_id=${botMessageId}`;

  const response = await fetch(streamUrl, {
    method: 'GET',
    headers: requestHeaders,
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Stream failed: ${response.status} - ${errorText}`);
  }

  return response.body;
}

/**
 * Build an AgentBuilderGraphSnapshot from current canvas nodes/edges.
 * Filters out UI-only nodes (start, seeq, note) and their connected edges.
 */
export function buildAgentBuilderGraphSnapshot(
  id: string,
  name: string,
  nodes: AgentBuilderNode[],
  edges: AgentBuilderEdge[],
  meta?: AgentBuilderMeta,
  versionId?: string,
): AgentBuilderGraphSnapshot {
  // Map seeq context nodes by id for later attachment
  const seeqNodeMap = new Map<string, SeeqNodeData>();
  nodes.forEach((n) => {
    if ((n.type || '') === 'seeq') {
      seeqNodeMap.set(n.id, (n.data ?? {}) as SeeqNodeData);
    }
  });

  // Capture all linked Seeq contexts per target node
  const contextsByTarget = new Map<string, SeeqContextPayload[]>();
  edges.forEach((e) => {
    if (e.data?.edgeType !== 'context') return;
    const ctxNode = seeqNodeMap.get(e.source);
    if (!ctxNode) return;
    const reference = parseSeeqContentReference(ctxNode.seeqUrl || '', AGENT_BUILDER_SEEQ_CONTENT_TYPES);
    const payload: SeeqContextPayload = {
      workbookId: ctxNode.workbookId || reference?.workbookId,
      worksheetId: ctxNode.worksheetId || reference?.worksheetId,
      projectId: ctxNode.projectId || reference?.projectId,
      folderId: ctxNode.folderId || reference?.folderId,
      seeqUrl: ctxNode.seeqUrl,
      title: ctxNode.title,
      contextNodeId: e.source,
    };
    const prev = contextsByTarget.get(e.target) || [];
    contextsByTarget.set(e.target, prev.concat(payload));
  });

  // Filter out UI-only nodes (start, seeq, note)
  const filteredNodes = nodes.filter((n) => !isUIOnlyNodeType(n.type));
  const filteredNodeIds = new Set(filteredNodes.map((n) => n.id));

  // Filter out edges connected to UI-only nodes
  const filteredEdges = edges.filter((e) => filteredNodeIds.has(e.source) && filteredNodeIds.has(e.target));

  const startNodeIds = new Set(nodes.filter((n) => n.type === 'start').map((n) => n.id));
  const entryNodeIds = Array.from(
    new Set(
      edges
        .filter((e) => startNodeIds.has(e.source) && e.data?.edgeType !== 'context')
        .map((e) => e.target)
        .filter((target) => filteredNodeIds.has(target)),
    ),
  );

  const snapshotMeta: AgentBuilderGraphMeta = {
    ...(meta ?? {}),
    entryNodeIds,
  };

  return {
    id,
    name,
    nodes: filteredNodes.map((n) => ({
      id: n.id,
      type: n.type,
      position: { x: n.position.x, y: n.position.y },
      data: (() => {
        const cloned = JSON.parse(JSON.stringify(n.data ?? {})) as Record<string, unknown>;
        const ctxList = contextsByTarget.get(n.id) || [];
        if (ctxList.length) {
          const compactList = ctxList.map((ctx) =>
            Object.fromEntries(Object.entries(ctx).filter(([, v]) => v !== undefined && v !== null)),
          );
          (
            cloned as Record<string, unknown> & {
              seeqContext?: Record<string, unknown>;
              seeqContexts?: Record<string, unknown>[];
            }
          ).seeqContexts = compactList;
          // Backward compatibility: also expose first context as seeqContext
          (cloned as Record<string, unknown> & { seeqContext?: Record<string, unknown> }).seeqContext = compactList[0];
        }
        return cloned;
      })(),
    })),
    edges: filteredEdges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle ?? undefined,
      targetHandle: e.targetHandle ?? undefined,
      type: e.type,
      data: e.data as Record<string, unknown> | undefined,
    })),
    versionId,
    meta: snapshotMeta,
  };
}

/**
 * Start an agent builder run. Returns the runId on success.
 * POST /genai/agent-workflows/runs
 */
export async function startAgentBuilderRun(
  payload: AgentBuilderRunStartPayload,
  signal?: AbortSignal,
  origin?: AgentBuilderOrigin,
): Promise<{ runId: string; chatId?: string; stream?: ReadableStream<Uint8Array> | null }> {
  const requestHeaders: Record<string, string> = {
    ...headers(origin),
    'Accept': APPSERVER_API_CONTENT_TYPE,
    'content-type': APPSERVER_API_CONTENT_TYPE,
  };

  const response = await fetch('/genai/agent-workflows/runs', {
    method: 'POST',
    headers: requestHeaders,
    body: JSON.stringify(payload),
    signal,
  });

  // If streaming, the response is a text stream; runId is in header
  const runIdFromHeader = response.headers.get('X-Workflow-Run-Id');
  const chatIdFromHeader = response.headers.get('X-Workflow-Chat-Id');

  if (!response.ok) {
    // Parse structured error responses from the backend.
    //
    // The backend returns all errors as JSON with a `detail` object:
    //   { "detail": { "code": "ERROR_CODE", "message": "...", "nodeIds": [...] } }
    //
    // We extract these fields into `AgentBuilderRunStartError` so callers can:
    // - Show feature-specific toasts based on `error.code` (e.g., UNREACHABLE_NODES)
    // - Highlight or scroll to invalid nodes using `error.nodeIds`
    // - Log `error.status` and `error.code` for telemetry without parsing strings
    const errorBody = (await response.json()) as { detail: AgentBuilderRunStartErrorDetail };
    const detail = errorBody.detail;
    const message = detail.message ?? `Agent builder run start failed: ${response.status}`;
    throw new AgentBuilderRunStartError(message, response.status, detail.code, detail.nodeIds);
  }

  if (payload.options.stream) {
    return { runId: runIdFromHeader || '', chatId: chatIdFromHeader || undefined, stream: response.body };
  }

  const body = await response.json();
  return { runId: body.runId || runIdFromHeader || '', chatId: body.chatId || chatIdFromHeader || undefined };
}

/**
 * Get the current status of an agent builder run.
 * GET /genai/agent-workflows/runs/{runId}
 */
export async function getAgentBuilderRunStatus(runId: string, signal?: AbortSignal): Promise<AgentBuilderRunStatus> {
  const response = await fetch(`/genai/agent-workflows/runs/${runId}`, {
    method: 'GET',
    headers: {
      ...headers(),
      Accept: APPSERVER_API_CONTENT_TYPE,
    },
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get agent builder run status: ${response.status} - ${errorText}`);
  }

  return response.json();
}

/**
 * Get the stream for an agent builder run (for async polling mode).
 * GET /genai/agent-workflows/runs/{runId}/stream
 */
export async function streamAgentBuilderRun(
  runId: string,
  signal?: AbortSignal,
): Promise<ReadableStream<Uint8Array> | null> {
  const response = await fetch(`/genai/agent-workflows/runs/${runId}/stream`, {
    method: 'GET',
    headers: {
      ...headers(),
      Accept: APPSERVER_API_CONTENT_TYPE,
    },
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Agent builder stream failed: ${response.status} - ${errorText}`);
  }

  return response.body;
}

/**
 * Get the thinking stream for an agent builder run (JSONL).
 * GET /genai/agent-workflows/runs/{runId}/stream/thinking
 */
export async function streamAgentBuilderThinking(
  runId: string,
  signal?: AbortSignal,
): Promise<ReadableStream<Uint8Array> | null> {
  const response = await fetch(`/genai/agent-workflows/runs/${runId}/stream/thinking`, {
    method: 'GET',
    headers: {
      ...headers(),
      Accept: APPSERVER_API_CONTENT_TYPE,
    },
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Agent builder thinking stream failed: ${response.status} - ${errorText}`);
  }

  return response.body;
}

/**
 * Cancel a running agent builder run.
 * POST /genai/agent-workflows/runs/{runId}/cancel
 */
export async function cancelAgentBuilderRun(
  runId: string,
  signal?: AbortSignal,
): Promise<{ runId: string; state: string }> {
  const response = await fetch(`/genai/agent-workflows/runs/${runId}/cancel`, {
    method: 'POST',
    headers: {
      ...headers(),
      Accept: APPSERVER_API_CONTENT_TYPE,
    },
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to cancel agent builder run: ${response.status} - ${errorText}`);
  }

  return response.json();
}

/**
 * Validate that the agent builder has a valid trigger type selected.
 * Returns true if a start node exists with a valid trigger type (time, condition, or chat).
 * For chat triggers with input_form sub-type, at least one field must be defined.
 */
export function validateAgentBuilderTrigger(nodes: AgentBuilderNode[]): boolean {
  const startNode = nodes.find((node) => node.type === 'start');
  if (!startNode || !startNode.data) return false;

  const data = startNode.data as StartNodeData;
  if (!data.triggerType) return false;

  // For chat triggers, a sub-type must be selected
  if (data.triggerType === 'chat') {
    if (!data.chatSubType) return false;

    // For input_form sub-type, require at least one field with a non-empty label,
    // and multiple_choice fields must have all options filled in
    if (data.chatSubType === 'input_form') {
      if (!Array.isArray(data.inputFormFields) || data.inputFormFields.length === 0) return false;

      return data.inputFormFields.every((field) => {
        if (field.label.trim() === '') return false;
        if (field.fieldType === 'multiple_choice') {
          return (
            Array.isArray(field.options) && field.options.length > 0 && field.options.every((opt) => opt.trim() !== '')
          );
        }
        return true;
      });
    }
  }

  return true;
}

export function canTargetUseFolderSeeqContext(targetType?: string): boolean {
  return !!targetType && FOLDER_SEEQ_CONTEXT_TARGET_TYPES.has(targetType);
}

export function canSeeqContextNodeUseFolderContent(
  contextNodeId: string,
  nodes: AgentBuilderNode[],
  edges: AgentBuilderEdge[],
): boolean {
  const contextEdges = edges.filter((edge) => edge.source === contextNodeId && edge.data?.edgeType === 'context');

  return (
    contextEdges.length > 0 &&
    contextEdges.every((edge) => {
      const targetNode = nodes.find((node) => node.id === edge.target);
      return canTargetUseFolderSeeqContext(targetNode?.type);
    })
  );
}

function hasSeeqContextContent(seeqNodeData: SeeqNodeData, allowFolderContent = false): boolean {
  const seeqUrl = seeqNodeData.seeqUrl || '';
  const reference = parseSeeqContentReference(seeqUrl, AGENT_BUILDER_SEEQ_CONTENT_TYPES);

  return Boolean(
    seeqNodeData.workbookId ||
    reference?.workbookId ||
    seeqNodeData.projectId ||
    reference?.projectId ||
    (allowFolderContent && (seeqNodeData.folderId || reference?.folderId)),
  );
}

/**
 * Returns connected Seeq node IDs that are missing required context content.
 * A Seeq context is valid when it resolves to a workbook, Data Lab project,
 * or folder for folder-aware targets.
 */
export function getInvalidConnectedSeeqNodeIds(nodes: AgentBuilderNode[], edges: AgentBuilderEdge[]): string[] {
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const invalidIds = new Set<string>();

  for (const edge of edges) {
    if (edge.data?.edgeType === 'context') {
      const sourceNode = nodeMap.get(edge.source);
      if (sourceNode && sourceNode.type === 'seeq') {
        const seeqNodeData = (sourceNode.data ?? {}) as SeeqNodeData;
        const targetNode = nodeMap.get(edge.target);
        if (!hasSeeqContextContent(seeqNodeData, canTargetUseFolderSeeqContext(targetNode?.type))) {
          invalidIds.add(sourceNode.id);
        }
      }
    }
  }

  return Array.from(invalidIds);
}

/**
 * Return true if the graph contains any invalid control-flow loops.
 * Valid loops must follow `SPEC/genai/WORKFLOW_LOOPS_SPEC.md`.
 */
export type WorkflowLoopValidationResult = { isValid: true } | { isValid: false; message: string };

const HANDLE_SLUG_PATTERN = /[^a-z0-9]+/g;

function slugHandle(value: string): string {
  const base = value.trim().toLowerCase();
  const slug = base.replace(HANDLE_SLUG_PATTERN, '-').replace(/^-+/, '').replace(/-+$/, '');
  return slug || 'option';
}

type SnapshotNode = AgentBuilderGraphSnapshot['nodes'][number];
type SnapshotEdge = AgentBuilderGraphSnapshot['edges'][number];

function nodeOrderIndex(nodes: SnapshotNode[]): Map<string, number> {
  const out = new Map<string, number>();
  nodes.forEach((n, idx) => out.set(n.id, idx));
  return out;
}

function classificationOptionsForNode(node: SnapshotNode): Array<{ id: string; label: string }> {
  if ((node.type || '') !== 'classify') return [];
  const data = node.data as Record<string, unknown>;
  const rawOptions = (data.classificationOptions ?? data.categories) as unknown;
  if (!Array.isArray(rawOptions)) return [];

  const options: Array<{ id: string; label: string }> = [];
  rawOptions.forEach((opt, idx) => {
    let label = '';
    let id = '';
    if (opt && typeof opt === 'object') {
      const dict = opt as Record<string, unknown>;
      const rawLabel = dict.label ?? dict.name ?? dict.value;
      if (typeof rawLabel === 'string') label = rawLabel.trim();
      const rawId = dict.id ?? dict.handle ?? dict.key;
      if (typeof rawId === 'string') id = rawId.trim();
    } else if (typeof opt === 'string') {
      label = opt.trim();
    }
    if (!label) return;
    const handleId = id || `${node.id}-option-${idx}-${slugHandle(label)}`;
    options.push({ id: handleId, label });
  });

  const ids = options.map((o) => o.id);
  if (new Set(ids).size !== ids.length) {
    throw new Error('Classify node category identifiers must be unique');
  }
  const labels = options.map((o) => o.label);
  if (new Set(labels).size !== labels.length) {
    throw new Error('Classify node categories must be unique');
  }
  return options;
}

function buildAdjacency(nodeIds: Set<string>, edges: SnapshotEdge[]): Map<string, string[]> {
  const adjacency = new Map<string, string[]>();
  for (const id of nodeIds) adjacency.set(id, []);
  for (const e of edges) {
    if (nodeIds.has(e.source) && nodeIds.has(e.target)) {
      adjacency.get(e.source)?.push(e.target);
    }
  }
  return adjacency;
}

function stronglyConnectedComponents(nodeIds: string[], adjacency: Map<string, string[]>): string[][] {
  let index = 0;
  const stack: string[] = [];
  const onStack = new Set<string>();
  const indices = new Map<string, number>();
  const lowlink = new Map<string, number>();
  const sccs: string[][] = [];

  const strongconnect = (v: string) => {
    indices.set(v, index);
    lowlink.set(v, index);
    index += 1;
    stack.push(v);
    onStack.add(v);

    for (const w of adjacency.get(v) ?? []) {
      if (!indices.has(w)) {
        strongconnect(w);
        lowlink.set(v, Math.min(lowlink.get(v) ?? 0, lowlink.get(w) ?? 0));
      } else if (onStack.has(w)) {
        lowlink.set(v, Math.min(lowlink.get(v) ?? 0, indices.get(w) ?? 0));
      }
    }

    if ((lowlink.get(v) ?? 0) !== (indices.get(v) ?? 0)) return;

    const scc: string[] = [];
    while (stack.length) {
      const w = stack.pop();
      if (!w) break;
      onStack.delete(w);
      scc.push(w);
      if (w === v) break;
    }
    sccs.push(scc);
  };

  for (const v of nodeIds) {
    if (!indices.has(v)) strongconnect(v);
  }

  return sccs;
}

function topoSort(nodes: string[], edges: Array<[string, string]>, order: Map<string, number>): string[] {
  const indegree = new Map<string, number>();
  const children = new Map<string, string[]>();
  nodes.forEach((n) => {
    indegree.set(n, 0);
    children.set(n, []);
  });

  const seen = new Set<string>();
  edges.forEach(([src, dst]) => {
    const key = `${src}->${dst}`;
    if (seen.has(key)) return;
    seen.add(key);
    children.get(src)?.push(dst);
    indegree.set(dst, (indegree.get(dst) ?? 0) + 1);
  });

  const ready: string[] = nodes.filter((n) => (indegree.get(n) ?? 0) === 0);
  ready.sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0) || a.localeCompare(b));

  const out: string[] = [];
  while (ready.length) {
    const cur = ready.shift();
    if (!cur) break;
    out.push(cur);
    const sortedChildren = (children.get(cur) ?? []).slice().sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
    sortedChildren.forEach((child) => {
      const next = (indegree.get(child) ?? 0) - 1;
      indegree.set(child, next);
      if (next === 0) {
        ready.push(child);
        ready.sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0) || a.localeCompare(b));
      }
    });
  }

  return out;
}

function validateLoopScc(
  scc: string[],
  nodeById: Map<string, SnapshotNode>,
  edges: SnapshotEdge[],
  adjacency: Map<string, string[]>,
  order: Map<string, number>,
): WorkflowLoopValidationResult {
  const sccSet = new Set(scc);
  const classifyNodes = scc.filter((id) => (nodeById.get(id)?.type || '') === 'classify');
  if (classifyNodes.length === 0) {
    return {
      isValid: false,
      message: 'Invalid workflow loop: every loop must include a Classify node that controls the loop.',
    };
  }

  const leavingEdgesBySource = new Map<string, SnapshotEdge[]>();
  edges.forEach((e) => {
    if (!sccSet.has(e.source) || sccSet.has(e.target)) return;
    const list = leavingEdgesBySource.get(e.source) ?? [];
    list.push(e);
    leavingEdgesBySource.set(e.source, list);
  });

  const controllerCandidates =
    leavingEdgesBySource.size > 0 ? classifyNodes.filter((id) => leavingEdgesBySource.has(id)) : classifyNodes;
  if (leavingEdgesBySource.size > 0 && controllerCandidates.length !== 1) {
    return {
      isValid: false,
      message: 'Invalid workflow loop: only one Classify node in a loop may connect to nodes outside the loop.',
    };
  }

  const validControllers: string[] = [];
  for (const controllerId of controllerCandidates) {
    // Only controller may leave SCC
    for (const [src] of leavingEdgesBySource.entries()) {
      if (src !== controllerId) {
        return {
          isValid: false,
          message: 'Invalid workflow loop: only the loop controller may connect to nodes outside the loop.',
        };
      }
    }

    const controllerNode = nodeById.get(controllerId);
    if (controllerNode) {
      let options: Array<{ id: string; label: string }> = [];
      try {
        options = classificationOptionsForNode(controllerNode);
      } catch (e) {
        return { isValid: false, message: (e as Error).message };
      }
      if (!options.length) {
        return {
          isValid: false,
          message: 'Invalid workflow loop: the loop controller must have at least one category.',
        };
      }
      const validHandleIds = new Set(options.map((o) => o.id));

      const insideByHandle = new Map<string, Set<string>>();
      const outsideByHandle = new Map<string, Set<string>>();
      edges.forEach((e) => {
        if (e.source !== controllerId) return;
        const handle = e.sourceHandle;
        if (!handle || !validHandleIds.has(handle)) {
          throw new Error('Invalid workflow loop: loop controller connections must come from one of its categories.');
        }
        if (sccSet.has(e.target)) {
          const set = insideByHandle.get(handle) ?? new Set<string>();
          set.add(e.target);
          insideByHandle.set(handle, set);
        } else {
          const set = outsideByHandle.get(handle) ?? new Set<string>();
          set.add(e.target);
          outsideByHandle.set(handle, set);
        }
      });

      for (const handle of validHandleIds) {
        const inside = insideByHandle.get(handle);
        const outside = outsideByHandle.get(handle);
        if (inside?.size && outside?.size) {
          return {
            isValid: false,
            message:
              'Invalid workflow loop: each loop controller category must either stay in the loop or exit the loop (not both).',
          };
        }
      }

      // Entry targets cannot have other in-loop parents besides controller.
      const inLoopParents = new Map<string, Set<string>>();
      edges.forEach((e) => {
        if (!sccSet.has(e.source) || !sccSet.has(e.target)) return;
        const parents = inLoopParents.get(e.target) ?? new Set<string>();
        parents.add(e.source);
        inLoopParents.set(e.target, parents);
      });

      insideByHandle.forEach((targets) => {
        targets.forEach((target) => {
          const parents = inLoopParents.get(target) ?? new Set<string>();
          const otherParents = Array.from(parents).filter((p) => p !== controllerId);
          if (otherParents.length) {
            throw new Error(
              'Invalid workflow loop: nodes the loop continues to must only be connected from the loop controller.',
            );
          }
        });
      });

      // Loop body DAG must be acyclic when removing controller->in-loop edges.
      const bodyEdges: Array<[string, string]> = [];
      edges.forEach((e) => {
        if (!sccSet.has(e.source) || !sccSet.has(e.target)) return;
        if (e.source === controllerId) return;
        bodyEdges.push([e.source, e.target]);
      });
      const bodyTopo = topoSort(scc, bodyEdges, order);
      if (bodyTopo.length !== scc.length) {
        return {
          isValid: false,
          message: 'Invalid workflow loop: the loop contains a cycle that does not go through the loop controller.',
        };
      }
      if (bodyTopo[bodyTopo.length - 1] !== controllerId) {
        return {
          isValid: false,
          message: 'Invalid workflow loop: every path in the loop must reach the loop controller.',
        };
      }

      validControllers.push(controllerId);
    }
  }

  if (validControllers.length !== 1) {
    return { isValid: false, message: 'Invalid workflow loop: a loop must have exactly one Classify loop controller.' };
  }
  return { isValid: true };
}

export function validateWorkflowLoops(graph: AgentBuilderGraphSnapshot): WorkflowLoopValidationResult {
  const nodeIds = graph.nodes.map((n) => n.id);
  const nodeById = new Map<string, SnapshotNode>(graph.nodes.map((n) => [n.id, n]));
  const nodeIdSet = new Set(nodeIds);
  const order = nodeOrderIndex(graph.nodes);

  const adjacency = buildAdjacency(nodeIdSet, graph.edges);
  const selfLoopNodes = new Set<string>();
  graph.edges.forEach((e) => {
    if (e.source === e.target) selfLoopNodes.add(e.source);
  });

  const sccs = stronglyConnectedComponents(nodeIds, adjacency);

  for (const rawScc of sccs) {
    const scc = Array.from(new Set(rawScc)).sort(
      (a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0) || a.localeCompare(b),
    );
    const isCyclic = scc.length > 1 || (scc.length === 1 && selfLoopNodes.has(scc[0] || ''));
    if (isCyclic) {
      try {
        const result = validateLoopScc(scc, nodeById, graph.edges, adjacency, order);
        if (!result.isValid) return result;
      } catch (e) {
        return { isValid: false, message: (e as Error).message || 'Invalid workflow loop.' };
      }
    }
  }

  return { isValid: true };
}

export function getWorkflowLoopValidationError(graph: AgentBuilderGraphSnapshot): string | null {
  const result = validateWorkflowLoops(graph);
  if (result.isValid) return null;
  if ('message' in result) return result.message;
  return 'Invalid workflow loop.';
}

/**
 * Build a stable signature for workflow-bearing canvas state. UI-only React
 * Flow fields such as selection, measurement, and dragging are intentionally
 * ignored so title-only undo/redo can avoid marking the workflow draft dirty.
 */
export function buildAgentBuilderWorkflowSignature(nodes: AgentBuilderNode[], edges: AgentBuilderEdge[]): string {
  const normalizedNodes = [...nodes]
    .map((node) => {
      const restData = { ...((node.data ?? {}) as Record<string, unknown>) };
      delete restData.status;
      return {
        id: node.id,
        type: node.type,
        position: {
          x: node.position?.x ?? 0,
          y: node.position?.y ?? 0,
        },
        data: JSON.parse(JSON.stringify(restData)),
      };
    })
    .sort((a, b) => a.id.localeCompare(b.id));

  const normalizedEdges = [...edges]
    .map((edge) => ({
      id: edge.id ?? '',
      source: edge.source,
      target: edge.target,
      type: edge.type,
      sourceHandle: edge.sourceHandle ?? null,
      targetHandle: edge.targetHandle ?? null,
      data: edge.data ? { edgeType: edge.data.edgeType } : undefined,
    }))
    .sort((a, b) => (a.id || '').localeCompare(b.id || ''));

  return JSON.stringify({
    nodes: normalizedNodes,
    edges: normalizedEdges,
  });
}

/**
 * Strip runtime-only fields (like status) from nodes before saving.
 * The status field is used for workflow execution visualization and should not be persisted.
 */
export function stripRuntimeFields(nodes: AgentBuilderNode[]): AgentBuilderNode[] {
  return nodes.map((node) => {
    if (!node.data) return node;

    const restData = { ...(node.data as Record<string, unknown> & { status?: unknown }) };
    delete restData.status;
    return {
      ...node,
      data: restData as typeof node.data,
    };
  });
}

const LEGACY_SEARCH_SEEQ_NODE_TYPES = new Set(['search-seeq-content', 'search-seeq-items', 'read-seeq-content']);
const SEARCH_SEEQ_NODE_TYPE = 'search-seeq';

/**
 * Normalize nodes by deep cloning to ensure mutability.
 * This removes layout properties (width/height/measured) that ReactFlow manages internally,
 * and creates fresh mutable objects that won't cause "Cannot assign to read only property" errors.
 *
 * Use this when loading nodes from an API response or Flux store (which may freeze objects).
 */
export function normalizeNodes(nodes: AgentBuilderNode[]): AgentBuilderNode[] {
  return nodes.map((node) => {
    const migratedType = LEGACY_SEARCH_SEEQ_NODE_TYPES.has(node.type || '') ? SEARCH_SEEQ_NODE_TYPE : node.type;
    const migratedData = JSON.parse(JSON.stringify(node.data ?? {})) as Record<string, unknown>;
    if (LEGACY_SEARCH_SEEQ_NODE_TYPES.has(String(migratedData.actionType))) {
      migratedData.actionType = SEARCH_SEEQ_NODE_TYPE;
    }
    return {
      id: node.id,
      type: migratedType,
      position: { x: node.position.x, y: node.position.y },
      data: migratedData as typeof node.data,
      // Preserve hidden flag for context nodes visibility toggle
      ...(node.hidden !== undefined && { hidden: node.hidden }),
    };
  }) as AgentBuilderNode[];
}

/**
 * Normalize edges by deep cloning to ensure mutability.
 * Creates fresh mutable objects that won't cause "Cannot assign to read only property" errors.
 *
 * Use this when loading edges from an API response or Flux store (which may freeze objects).
 */
export function normalizeEdges(edges: AgentBuilderEdge[]): AgentBuilderEdge[] {
  return edges.map((edge) => ({
    ...edge,
    // Use default edge rendering for workflow edges by clearing type
    type: edge.data?.edgeType === 'workflow' ? undefined : edge.type,
    // Set the appropriate marker based on edge type (context edges get purple marker)
    markerEnd: edge.data?.edgeType === 'context' ? CONTEXT_EDGE_MARKER : DEFAULT_EDGE_MARKER,
    data: edge.data ? JSON.parse(JSON.stringify(edge.data)) : undefined,
    // Preserve hidden flag for context edges visibility toggle
    ...(edge.hidden !== undefined && { hidden: edge.hidden }),
  }));
}

/** Handle IDs that indicate a context connection */
const CONTEXT_HANDLE_IDS = ['seeq-context-output', 'agent-context'];

/**
 * Determines if a connection is a context connection based on source/target handles.
 */
export function isContextConnection(sourceHandle?: string | null, targetHandle?: string | null): boolean {
  return (
    (sourceHandle !== undefined && sourceHandle !== null && CONTEXT_HANDLE_IDS.includes(sourceHandle)) ||
    (targetHandle !== undefined && targetHandle !== null && CONTEXT_HANDLE_IDS.includes(targetHandle))
  );
}

/**
 * Gets the edge type based on connection details.
 * @returns 'context' | 'classify' | 'workflow'
 */
export function getEdgeTypeForConnection(
  sourceHandle?: string | null,
  targetHandle?: string | null,
  isClassifySource = false,
): 'context' | 'classify' | 'workflow' {
  if (isContextConnection(sourceHandle, targetHandle)) return 'context';
  if (isClassifySource) return 'classify';
  return 'workflow';
}

/**
 * Gets the React Flow edge type (for custom rendering) based on connection details.
 * Returns undefined for workflow edges to use default rendering.
 */
export function getReactFlowEdgeType(
  sourceHandle?: string | null,
  targetHandle?: string | null,
  isClassifySource = false,
): 'context' | 'classify' | undefined {
  if (isContextConnection(sourceHandle, targetHandle)) return 'context';
  if (isClassifySource) return 'classify';
  return undefined;
}

/**
 * Gets the appropriate marker for an edge based on connection details.
 */
export function getEdgeMarker(sourceHandle?: string | null, targetHandle?: string | null) {
  if (isContextConnection(sourceHandle, targetHandle)) return CONTEXT_EDGE_MARKER;
  return DEFAULT_EDGE_MARKER;
}

// ============================================================================
// Run Start Error Utilities
// ============================================================================

/**
 * Extract error detail from an AgentBuilderRunStartError.
 * Returns null if the error is not an AgentBuilderRunStartError.
 *
 * Use this helper in both manual and chat run error handling to:
 * - Get the list of node IDs that caused validation failures
 * - Show appropriate error messages based on error code
 * - Update node statuses consistently across both flows
 */
export function extractRunStartErrorDetail(error: unknown): AgentBuilderRunStartErrorDetail | null {
  if (!(error instanceof AgentBuilderRunStartError)) {
    return null;
  }

  return {
    code: error.code,
    message: error.message,
    nodeIds: error.nodeIds,
  };
}

/**
 * Apply error status to nodes identified in a run-start error.
 * This is a convenience helper that calls a status setter for each node ID in the error.
 *
 * @param error - The error to extract node IDs from
 * @param setNodeStatus - Callback to set status for a single node
 * @returns The extracted error detail, or null if not a run-start error
 */
export function applyRunStartErrorToNodes(
  error: unknown,
  setNodeStatus: (nodeId: string, status: 'error') => void,
): AgentBuilderRunStartErrorDetail | null {
  const errorDetail = extractRunStartErrorDetail(error);
  if (!errorDetail) return null;

  if (errorDetail.nodeIds?.length) {
    for (const nodeId of errorDetail.nodeIds) {
      setNodeStatus(nodeId, 'error');
    }
  }

  return errorDetail;
}

// ============================================================================
// Node Position Utilities
// ============================================================================

/**
 * Find an available position for a new node that doesn't overlap with existing nodes.
 * Starts from the viewport center and spirals outward to find a free position.
 *
 * @param nodes - Current nodes on the canvas
 * @param viewportCenter - Center of the current viewport in flow coordinates
 * @param gridSize - Grid spacing for position checks (default 200px)
 * @param nodeSize - Approximate size of a node for collision detection (default 180px)
 * @returns A position {x, y} that doesn't overlap with existing nodes
 */
export function findAvailablePosition(
  nodes: AgentBuilderNode[],
  viewportCenter: { x: number; y: number },
  gridSize = 200,
  nodeSize = 180,
): { x: number; y: number } {
  // Helper to check if a position overlaps with any existing node
  const isPositionOccupied = (x: number, y: number): boolean => {
    return nodes.some((node) => {
      const dx = Math.abs(node.position.x - x);
      const dy = Math.abs(node.position.y - y);
      // Check if nodes would overlap (using nodeSize as bounding box)
      return dx < nodeSize && dy < nodeSize;
    });
  };

  // Start at viewport center
  const startX = Math.round(viewportCenter.x / gridSize) * gridSize;
  const startY = Math.round(viewportCenter.y / gridSize) * gridSize;

  // If center is available, use it
  if (!isPositionOccupied(startX, startY)) {
    return { x: startX, y: startY };
  }

  // Spiral outward to find an available position
  // Check positions in expanding rings around the center
  const maxRings = 10;
  for (let ring = 1; ring <= maxRings; ring++) {
    // Check all positions in current ring
    for (let dx = -ring; dx <= ring; dx++) {
      for (let dy = -ring; dy <= ring; dy++) {
        // Only check positions on the perimeter of the ring
        if (Math.abs(dx) === ring || Math.abs(dy) === ring) {
          const candidateX = startX + dx * gridSize;
          const candidateY = startY + dy * gridSize;

          if (!isPositionOccupied(candidateX, candidateY)) {
            return { x: candidateX, y: candidateY };
          }
        }
      }
    }
  }

  // Fallback: if all positions are occupied (unlikely), offset from center
  return { x: startX + gridSize, y: startY + gridSize };
}

// ============================================================================
// Agent Builder Pin/Favorite Utilities
// ============================================================================

export async function getPinnedAgentIds(): Promise<Set<string>> {
  try {
    const response = await sqItemsApi.getPinnedItems({});
    const items = response.data.items ?? [];
    return new Set(items.map((item) => item.id));
  } catch (error) {
    logError('Error fetching pinned agent IDs:', String(error));
    return new Set();
  }
}

export async function pinAgent(agentId: string): Promise<void> {
  try {
    await sqItemsApi.pinItem({ itemId: agentId });
  } catch (error) {
    logError(`Error pinning agent ${agentId}:`, String(error));
  }
}

export async function unpinAgent(agentId: string): Promise<void> {
  try {
    await sqItemsApi.unpinItem({ itemId: agentId });
  } catch (error) {
    logError(`Error unpinning agent ${agentId}:`, String(error));
  }
}

// ============================================================================
// Agent Builder Navigation Utilities
// ============================================================================

/**
 * Navigates to an Agent Builder path, honoring the user's "open links in a new tab" preference
 * (`sqWorkbenchStore.preferNewTab`) and ctrl/cmd-click. This mirrors the navigation behavior used
 * elsewhere in the app (e.g. `openProject` and `HomeScreenNewButton`).
 *
 * @param path - the in-app path to open (e.g. `/agent-builder/123`)
 * @param navigate - the react-router navigate function used for same-tab navigation
 * @param event - optional click/key event used to detect ctrl/cmd-click
 * @returns true if the path was opened in a new tab, false if navigated within the current tab
 */
export function openAgentBuilder(
  path: string,
  navigate: NavigateFunction,
  event?: React.MouseEvent | React.KeyboardEvent,
): boolean {
  const openInNewTab = sqWorkbenchStore.preferNewTab || Boolean(event?.ctrlKey) || Boolean(event?.metaKey);
  if (openInNewTab) {
    window.open(path, '_blank');
    return true;
  }
  navigate(path);
  return false;
}
